import {
  Box,
  PairingView,
  type PairingViewLabels,
  type PairingViewState,
} from "@reflecto/client-ui";
import type { PairStatusConfirmed } from "@reflecto/core";
import { useCallback, useEffect, useRef, useState } from "react";
import { webStorage } from "../adapters/storage.web";
import {
  initPairing,
  openPairingStream,
  type PairingStream,
  pollPairingStatus,
} from "../api/client";
import { messages } from "../i18n";
import { logError } from "../lib/log";
import { parsePairingIntent } from "../lib/router";
import { trackPairingCodeDisplayed, trackPairingCompleted } from "../services/analytics";
import { ensureKeypair } from "../services/keys";
import {
  cancelPairing,
  completePairing,
  PairingRejectedError,
  pollUntilConfirmed,
} from "../services/pairing";

// Labels are sourced from the web's i18n catalog (Phase F lift). The
// `pairingFailed` key is a contextual heading override for the error
// / expired states — without it the heading reads "Enter this code in
// the Reflecto app" while the body shows a red error panel + retry
// button, a contradictory directive that the legacy pre-lift
// PairingPage avoided by branching on the local FSM state. The
// extension wrapper omits this override (its pre-Phase-E shape used a
// single non-contextual heading too), so the headless falls back to
// enterCodeInstruction in that case. Same strings as the extension's
// `_locales/en/messages.json` so the experience reads identically.
const LABELS: PairingViewLabels = {
  enterCodeInstruction: messages.pairing.enterCodeInstruction,
  pairedSuccessfully: messages.pairing.pairedSuccessfully,
  pairingFailed: messages.pairing.pairingFailed,
  requestingPairingCode: messages.pairing.requestingPairingCode,
  codeExpired: messages.pairing.codeExpired,
  tryAgain: messages.pairing.tryAgain,
  failedToStartPairing: messages.pairing.failedToStartPairing,
  // Install + capability education (spec P2/P3/P5/P7). The headless
  // component renders this section only in the loading / showCode states
  // (P8) and only when `installUrl` + `installHeading` are both supplied.
  installHeading: messages.pairing.installHeading,
  installScanHint: messages.pairing.installScanHint,
  installQrLabel: messages.pairing.installQrLabel,
  capabilitiesHeading: messages.pairing.capabilitiesHeading,
  capabilities: [
    messages.pairing.capabilityMirror,
    messages.pairing.capabilityReply,
    messages.pairing.capabilityShare,
  ],
  encryptionNote: messages.pairing.encryptionNote,
};

/**
 * Canonical Play listing encoded into the install QR (spec P4).
 *
 * Deliberately the full canonical URL — no shortener, no redirect
 * service, no dynamic link. Every indirection is a dependency with its
 * own failure mode, and the Firebase Dynamic Links sunset (25 Aug 2025;
 * every FDL link now 404s) is the live example of what that costs.
 *
 * A prop rather than a locale string: it is not translated copy and must
 * not drift per locale.
 *
 * SECURITY: this is the ONLY thing the QR ever encodes. The 6-digit
 * pairing code is a short-lived secret and must never reach a QR, a URL,
 * or a log.
 */
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=dev.reflecto.app";

/**
 * Short human-typable install URL rendered as selectable text beside the
 * QR (spec P5) — for phones whose camera app does not scan QR codes, and
 * for users who would rather type. Rendered without a scheme because it
 * is typed into a phone, not clicked.
 *
 * ⚠️ RELEASE GATE (spec §6). `https://reflecto.dev/get` returned **404**
 * as of 2026-08-06. Its 302 to `PLAY_STORE_URL` must be live BEFORE this
 * reaches users, or first-time users — the exact audience this whole
 * section exists for — land on a dead page, which is strictly worse than
 * the state this replaces.
 *
 * If the redirect is not ready at release time, the fallback is this one
 * line: change it to `"reflecto.dev"` (the apex exists today). The QR is
 * unaffected either way — it always encodes `PLAY_STORE_URL`, so
 * scanning works regardless of whether the redirect shipped. Only the
 * typed path depends on it.
 */
const INSTALL_TYPED_URL = "reflecto.dev/get";

/**
 * Web-native pairing screen. Same headless `PairingView` UI as the
 * extension, plus a web-specific orchestration layer:
 *
 * The web app is a single React tree with no IPC layer between UI
 * and business logic, so the keypair-gen + initPairing call + the
 * SSE/poll race all run inline here. Everything visual is delegated
 * to `@reflecto/client-ui`'s `PairingView`.
 *
 * State machine:
 *   loading → showCode → confirmed    (happy path, Android confirms)
 *   loading → showCode → expired      (countdown reaches zero)
 *   loading → error                   (network / server failure)
 *
 * Confirmation races the pairing SSE stream against a 2-second poll
 * loop; whichever sees it first wins. An AbortController tears both
 * down on unmount or retry.
 */
export function PairingPage() {
  const [state, setState] = useState<PairingViewState>("loading");
  const [code, setCode] = useState("");
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");

  // Whether the next-minted code should carry `intent: "assistant"`
  // (spec O2, amended 2026-08-07). No more UI toggle — the intent is
  // read from the URL exactly once, at mount, via a lazy `useState`
  // initializer (React only invokes this function on the initial
  // render, even under StrictMode's double-invoke). A ref would work
  // too, but the lazy-init form makes "read once, never again" the
  // literal shape of the code rather than a convention callers must
  // uphold. `startPairing` closes over `intent` directly — it can
  // never change out from under an in-flight or retried mint.
  const [intent] = useState(() => parsePairingIntent());

  // All side-effect handles live in refs so a single `teardown()` call
  // stops everything — timers, polls, SSE stream. The flow otherwise
  // has three concurrent actors that each need their own cleanup.
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollAbortRef = useRef<AbortController | null>(null);
  const streamRef = useRef<PairingStream | null>(null);

  // Generation guard: `teardown()` can cancel established resources
  // (countdown/poll/stream) but not an already-in-flight
  // `ensureKeypair()`/`initPairing()` fetch — those keep running to
  // completion regardless. Without this guard, a retry mid-mint (or
  // StrictMode's double-invoked mount effect below) races two
  // `startPairing()` calls, and whichever resolves LAST wins the
  // displayed code/state/resources — even if it's the stale call.
  // Every `startPairing` invocation claims the next generation number;
  // after each `await` that precedes a state write or resource
  // registration, a stale call checks its claimed generation against
  // the current one and bails silently (no state write, no resource
  // setup) if a newer mint has since started.
  const mintGenerationRef = useRef(0);

  const teardown = useCallback(() => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    if (pollAbortRef.current) {
      pollAbortRef.current.abort();
      pollAbortRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.abort();
      streamRef.current = null;
    }
  }, []);

  const startPairing = useCallback(async () => {
    const generation = ++mintGenerationRef.current;
    teardown();
    setErrorMsg("");
    setCode("");
    setState("loading");

    try {
      const publicKey = await ensureKeypair();
      // A newer mint (retry / StrictMode re-invoke) started while this
      // awaited — discard silently, don't touch state or resources.
      if (generation !== mintGenerationRef.current) return;

      const init = await initPairing(publicKey, intent);
      if (generation !== mintGenerationRef.current) return;

      await webStorage.set({ deviceId: init.extensionDeviceId });
      if (generation !== mintGenerationRef.current) return;

      setCode(init.code);
      setSecondsLeft(init.expiresIn);
      setState("showCode");
      trackPairingCodeDisplayed();

      // Countdown. The interval self-stops the moment it ticks to
      // zero so we don't wait a render cycle for the
      // `secondsLeft === 0` effect at the bottom of this file to
      // call `teardown()`. Without this, after the first 0-tick the
      // interval keeps firing at 1Hz between the zero render and
      // the effect's cleanup commit — `Math.max(0, 0-1) === 0` so
      // the redundant ticks are harmless (React bails on the
      // same-value setSecondsLeft) but they're wasted work and
      // diverge from the pre-Phase-E self-stopping pattern.
      countdownRef.current = setInterval(() => {
        setSecondsLeft((prev) => {
          const next = Math.max(0, prev - 1);
          if (next === 0 && countdownRef.current) {
            clearInterval(countdownRef.current);
            countdownRef.current = null;
          }
          return next;
        });
      }, 1000);

      // Race an SSE stream and a 2-second poll. Whichever confirms
      // first wins; the AbortController stops the loser.
      const abort = new AbortController();
      pollAbortRef.current = abort;

      // Local capture: the post-race cleanup below aborts THIS call's stream only —
      // reading the shared ref there could abort a newer mint's stream if a stale
      // confirmation settles in the same tick as a retry-triggered supersession.
      const myStream = openPairingStream(init.extensionDeviceId);
      streamRef.current = myStream;
      // When the stream fires "confirmed", we still need to fetch the
      // credentials — the stream only signals, it doesn't carry them.
      // One fetch via pollPairingStatus (not the loop) is enough; the
      // server's confirmed state is deterministic post-signal. If that
      // single fetch returns pending (narrow race with the server),
      // hand off to viaPoll's loop to catch up.
      //
      // If the stream rejects (expired, network blip, proxy drop),
      // yield to the poll via a never-settling sentinel. viaPoll's
      // own rejection will always settle Promise.race — the sentinel
      // only hangs if viaPoll is still running, in which case the
      // race just waits for the poll to win or be aborted.
      const viaStream = streamRef.current.confirmed
        .then(async (): Promise<PairStatusConfirmed> => {
          const status = await pollPairingStatus(init.extensionDeviceId);
          if (status.status === "confirmed") return status;
          // The server pushes the same wake-up event for a terminal rejection
          // (#339), so this fetch legitimately comes back `rejected`. Settle the
          // race now rather than yielding to viaPoll, which would sit on the
          // sentinel for up to a full poll interval before reporting the same
          // thing. `pending` (a narrow race with the server) still hands off.
          if (status.status === "rejected") throw new PairingRejectedError(status.reason);
          return abortOnlySentinel(abort.signal);
        })
        .catch((err: unknown): Promise<PairStatusConfirmed> => {
          // Terminal rejections must propagate; stream failures (expired,
          // network blip, proxy drop) still yield to the poll.
          if (err instanceof PairingRejectedError) throw err;
          return abortOnlySentinel(abort.signal);
        });
      const viaPoll = pollUntilConfirmed(init.extensionDeviceId, { signal: abort.signal });

      // `.finally` rather than two statements after the await: the race can now REJECT
      // with a real error (PairingRejectedError), not just the AbortError that only ever
      // arrived after teardown had already aborted both legs. Bare statements are skipped
      // on that path, leaving the losing poll to fire one more request before its own tick
      // saw the same sticky status. Aborting `abort`/`myStream` — the locally captured
      // pair, never the shared ref — keeps a newer mint's legs untouched.
      const confirmed = await Promise.race([viaStream, viaPoll]).finally(() => {
        abort.abort();
        myStream.abort();
      });
      if (generation !== mintGenerationRef.current) return;

      await completePairing(confirmed);
      if (generation !== mintGenerationRef.current) return;
      void trackPairingCompleted(intent ?? "mirror");
      // `confirmed` UI is rendered via the `paired` storage flag,
      // which the App router watches — this transition is brief and
      // mostly for sr-only announcement before the App router swaps
      // out to MainPage.
      setState("confirmed");
    } catch (err: unknown) {
      // A newer mint superseded this one — whether the error is a
      // teardown-triggered abort or something else entirely, a
      // superseded attempt never gets to write state.
      if (generation !== mintGenerationRef.current) return;
      if (isAbortLike(err)) {
        // Expected teardown (user retried / unmounted / expired).
        return;
      }
      // Fall back to the generic copy when the thrown Error has an
      // empty message; otherwise the sighted-user error panel renders
      // blank while the ARIA live region still announces "Pairing
      // failed" — a confusing mismatch.
      const message =
        err instanceof Error && err.message ? err.message : LABELS.failedToStartPairing;
      setErrorMsg(message);
      setState("error");
      // Cleanup is best-effort — surfacing a quota/IDB error here
      // would overwrite the real error already on screen, so log and
      // move on. Routed through `logError` so the structured-log
      // sink (and any future Sentry/OTLP wiring) sees it instead of
      // a bare console.error that the security-rules audit can't
      // grep for.
      cancelPairing().catch((cleanupErr: unknown) => {
        logError("pairing", "cancel_pairing_cleanup_failed", cleanupErr);
      });
    }
  }, [teardown, intent]);

  // Transition to expired when the countdown hits zero.
  useEffect(() => {
    if (state === "showCode" && secondsLeft === 0) {
      teardown();
      setState("expired");
      void cancelPairing();
    }
  }, [state, secondsLeft, teardown]);

  // Kick off the flow on mount. StrictMode double-invokes effects —
  // teardown() on the first cleanup cancels the in-flight attempt
  // before the second invocation restarts, so no duplicate deviceIds
  // linger on the server.
  useEffect(() => {
    void startPairing();
    return teardown;
  }, [startPairing, teardown]);

  return (
    // min-height:0 lets this flex child shrink so overflow-y:auto scrolls when PairingView overflows its slot.
    <Box direction="column" style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
      <PairingView
        state={state}
        code={code}
        secondsLeft={secondsLeft}
        errorMessage={errorMsg}
        onRetry={startPairing}
        labels={LABELS}
        installUrl={PLAY_STORE_URL}
        installTypedUrl={INSTALL_TYPED_URL}
      />
    </Box>
  );
}

/**
 * Sentinel used to "yield" one leg of the pairing race while the
 * other keeps running. Stays pending until the shared AbortController
 * fires, at which point it rejects with AbortError — so Promise.race
 * always settles when teardown fires, even if both legs would
 * otherwise sit in a hung catch. Compared to a truly never-settling
 * sentinel, this removes the edge case where viaPoll hypothetically
 * never rejects (e.g. a test mocking pollPairingStatus to resolve
 * "pending" forever with no abort).
 */
function abortOnlySentinel(signal: AbortSignal): Promise<never> {
  return new Promise<never>((_, reject) => {
    if (signal.aborted) {
      reject(new DOMException("Pairing flow aborted", "AbortError"));
      return;
    }
    signal.addEventListener(
      "abort",
      () => reject(new DOMException("Pairing flow aborted", "AbortError")),
      { once: true }
    );
  });
}

/**
 * True when `err` represents an expected teardown rather than a real
 * failure. Two shapes are recognised:
 *
 *   - `DOMException(name = "AbortError")` — native fetch/stream abort.
 *   - `Error("Pairing stream aborted")` — `openPairingStream` normalises
 *     the DOMException to this stable Error at the client boundary so
 *     callers can branch on the message. Either representation can
 *     surface here depending on which race leg aborted.
 */
function isAbortLike(err: unknown): boolean {
  if (err instanceof DOMException && err.name === "AbortError") return true;
  if (err instanceof Error && err.message === "Pairing stream aborted") return true;
  return false;
}
